import {
  Box,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalCloseButton,
  ModalOverlay,
  Button,
} from "@chakra-ui/react";
import { useRef, useState, useEffect } from "react";
// @ts-ignore
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { ScaleableImage } from "@root/components/scaleableImage/scaleableImage";
import { ScaleableSliderImages } from "@root/components/scaleableSliderImages";
import ImageSliderFeature from "@root/components/imageSlider";
import { ImageLoader } from "@root/assets/icons/imageLoader";
import { CenterFlex } from "./style";
import { RightIcon } from "@root/assets/icons/rightIcon";
import { LeftIcon } from "@root/assets/icons/leftIcon";
import Breakpoints from "@root/components/mediaQuery";
import { DownloadIcon } from "@root/assets/icons";
import { downloadImageFromServer } from "@root/utils/download-image";
import { useTranslation } from "react-i18next";

// Custom Next Arrow Component
const NextArrow = ({ onClick }: any) => (
  <CenterFlex
    width={"48px"}
    height={"48px"}
    position="absolute"
    left="105%"
    top="50%"
    transform="translate(0, -50%)"
    zIndex={2}
    borderRadius={"8px"}
    cursor={"pointer"}
    userSelect={"none"}
    onClick={onClick}
  >
    <RightIcon />
  </CenterFlex>
);

const PrevArrow = ({ onClick }: any) => (
  <CenterFlex
    width={"48px"}
    height={"48px"}
    position="absolute"
    right="105%"
    top="50%"
    transform="translate(0, -50%)"
    zIndex={2}
    borderRadius={"8px"}
    cursor={"pointer"}
    userSelect={"none"}
    onClick={onClick}
  >
    <LeftIcon />
  </CenterFlex>
);

export const ImageSliderModal = ({
  isOpen,
  onClose,
  selectedIndex = 0,
  data,
}: any) => {
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const { md, lg, xl, xxl } = Breakpoints();
  const sliderRef: any = useRef(null);
  const [scale, setScale] = useState(0.8);
  const canvasRef = useRef(null);
  const [imageDownloading, setImageDownloading] = useState(false);
  const { t } = useTranslation("common");

  const incrementScale = () => {
    if (scale < 10) {
      // Set an upper limit for zoom-in
      setScale((prevScale) => Math.min(prevScale + 0.1, 10)); // Increment by 0.1, max 10
    }
  };

  const decrementScale = () => {
    if (scale <= 0.6) return;
    if (scale > 0.6) {
      // Set a lower limit for zoom-out
      setScale((prevScale) => Math.max(prevScale - 0.1, 0.1)); // Decrement by 0.1, min 0.1
    }
  };

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: md || lg || xl || xxl,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (_: any, next: any) => setMainImageIndex(next),
  };

  useEffect(() => {
    setMainImageIndex(selectedIndex);
  }, [selectedIndex, sliderRef]);

  useEffect(() => {
    // Trigger only if sliderRef has a valid current value
    if (sliderRef?.current) {
      sliderRef.current.slickGoTo(selectedIndex); // Go to the selected index slide
    }
  }, [sliderRef.current]);

  const goToSlide = (index: any) => {
    setScale(0.8);
    if (sliderRef?.current && sliderRef?.current?.slickGoTo) {
      sliderRef.current.slickGoTo(index); // Use ref to call slickGoTo()
      setMainImageIndex(index);
    }
  };

  const handleDownload = () => {
    const url = data[mainImageIndex];
    downloadImageFromServer(
      url,
      canvasRef,
      setImageDownloading,
      imageDownloading
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setScale(0.8);
        onClose();
      }}
      onOverlayClick={() => {
        setScale(0.8);
        onClose();
      }}
      size="4xl"
    >
      <ModalOverlay />
      <ModalContent m={"auto"} bgColor={"white"}>
        <ModalHeader
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap="30px"
        >
          <Flex mr={"30px"}>
            <Button
              size="sm"
              fontSize={"14px"}
              fontWeight="500"
              color="gray.neutral"
              bgColor={"theme.white"}
              border={"1px solid"}
              borderColor={"gray.200"}
              borderRadius={"8px"}
              onClick={handleDownload}
              isDisabled={imageDownloading}
              padding={"6px 12px"}
            >
              <DownloadIcon mr={2} />
              {t("download.btnTitle")}
            </Button>
          </Flex>
          <ModalCloseButton ml={2} />{" "}
        </ModalHeader>

        <ModalBody padding={"24px"} borderRadius={"16px"}>
          {/* Main Image Slider */}
          {data?.length === 1 ? (
            <Box w="full" flex={1} position="relative">
              {data?.map((img: any, index: number) => (
                <Box
                  h={"100%"}
                  key={index}
                  borderRadius={"8px"}
                  overflow={"hidden"}
                >
                  <ScaleableImage src={img} scale={scale} />
                  <ImageSliderFeature
                    scale={scale}
                    setScale={setScale}
                    incrementScale={incrementScale}
                    decrementScale={decrementScale}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <Box w="full" flex={1} position="relative">
              <Slider ref={sliderRef} {...sliderSettings}>
                {data?.map((img: any, index: number) => (
                  <Box
                    h={"100%"}
                    key={index}
                    borderRadius={"8px"}
                    overflow={"hidden"}
                  >
                    <ScaleableSliderImages src={img} scale={scale} />
                  </Box>
                ))}
              </Slider>
              <ImageSliderFeature
                scale={scale}
                setScale={setScale}
                incrementScale={incrementScale}
                decrementScale={decrementScale}
              />
            </Box>
          )}

          {/* Thumbnails */}
          <Flex gap={"10px"} mt={"12px"} width={"100%"} overflow={"scroll"}>
            {data?.map((img: any, index: number) => (
              <Box
                key={index}
                userSelect={"none"}
                cursor="pointer"
                border={
                  mainImageIndex === index
                    ? "2px solid blue"
                    : "2px solid transparent"
                }
                onClick={() => {
                  goToSlide(index);
                }}
                minW={"136px"}
                maxH={"77px"}
                borderRadius={"5px"}
                overflow={"hidden"}
              >
                <Image
                  fallback={<ImageLoader />}
                  src={img}
                  alt={`Thumbnail ${index}`}
                  w="100%"
                  h="60px"
                  objectFit="cover"
                />
              </Box>
            ))}
          </Flex>
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
